import ListItem from '@tiptap/extension-list-item';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * Issue #141 follow-up: Aufzaehlungs-Marker (1., 2., •) reagierten bisher
 * nicht auf Bold/Italic/Schriftgroesse. Grund: TipTap rendert den FontSize-
 * /Bold-Mark als <span style="..."> bzw. <strong> INNERHALB des <li>;
 * der Marker wird vom Browser aber anhand des <li>-Elements selbst
 * gerendert, nicht anhand des inneren Spans.
 *
 * Diese Extension haengt den dominanten Style des li-Inhalts zusaetzlich
 * als inline-Style aufs <li>. Plus CSS `li::marker { font-size: inherit;
 * font-weight: inherit; font-style: inherit; }` (im Editor + Player) —
 * der Marker erbt dann die richtige Darstellung.
 *
 * Heuristik:
 *  - fontSize: wenn alle Text-Nodes im li dieselbe fontSize haben, gewinnt die.
 *  - bold/italic: wenn alle Text-Nodes diesen Mark tragen, ist er aktiv.
 *  - Sonst: Attribut bleibt null, Marker rendert in Default-Style.
 *
 * Gemischte Selektionen ("nur ein Wort fett im li") aendern den Marker
 * absichtlich NICHT — die Mehrheit der User-Erwartungen ist "ganze Zeile
 * fett → Marker fett".
 */
export const ListItemWithStyle = ListItem.extend({
    addAttributes() {
        const parentAttrs = (this as any).parent?.() ?? {};
        return {
            ...parentAttrs,
            fontSize: {
                default: null as string | null,
                parseHTML: (el: HTMLElement) => el.getAttribute('data-font-size'),
                renderHTML: (attrs: any) => {
                    if (!attrs.fontSize) return {};
                    return {
                        'data-font-size': attrs.fontSize,
                        style: appendStyle(attrs.style, `font-size: ${attrs.fontSize}`),
                    };
                },
            },
            bold: {
                default: false,
                parseHTML: (el: HTMLElement) => el.getAttribute('data-bold') === 'true',
                renderHTML: (attrs: any) => {
                    if (!attrs.bold) return {};
                    return {
                        'data-bold': 'true',
                        style: appendStyle(attrs.style, 'font-weight: bold'),
                    };
                },
            },
            italic: {
                default: false,
                parseHTML: (el: HTMLElement) => el.getAttribute('data-italic') === 'true',
                renderHTML: (attrs: any) => {
                    if (!attrs.italic) return {};
                    return {
                        'data-italic': 'true',
                        style: appendStyle(attrs.style, 'font-style: italic'),
                    };
                },
            },
        };
    },

    addProseMirrorPlugins() {
        const parentPlugins = ((this as any).parent?.() ?? []) as any[];
        return [
            ...parentPlugins,
            new Plugin({
                key: new PluginKey('listItemWithStyle-attrSync'),
                /**
                 * Nach jeder Transaktion: durchsuche alle list items, deren
                 * Inhalt sich geaendert hat, und synchronisiere ihre
                 * fontSize/bold/italic-Attribute mit dem Inhalt.
                 */
                appendTransaction: (transactions, oldState, newState) => {
                    const docChanged = transactions.some(tr => tr.docChanged);
                    if (!docChanged) return null;

                    let tr = newState.tr;
                    let changed = false;

                    newState.doc.descendants((node, pos) => {
                        if (node.type.name !== 'listItem') return;

                        const dominant = computeDominantStyle(node);
                        const current = {
                            fontSize: node.attrs.fontSize ?? null,
                            bold: node.attrs.bold ?? false,
                            italic: node.attrs.italic ?? false,
                        };

                        if (
                            dominant.fontSize !== current.fontSize ||
                            dominant.bold !== current.bold ||
                            dominant.italic !== current.italic
                        ) {
                            tr = tr.setNodeMarkup(pos, undefined, {
                                ...node.attrs,
                                ...dominant,
                            });
                            changed = true;
                        }
                    });

                    return changed ? tr : null;
                },
            }),
        ];
    },
});

/**
 * Berechnet den dominanten Inline-Style fuer ein listItem.
 *
 * - fontSize: wenn ALLE Text-Nodes (rekursiv durch span/strong/em) die
 *   gleiche fontSize tragen, gewinnt die. Sonst null.
 * - bold/italic: nur true wenn ALLE Text-Nodes den Mark tragen.
 *
 * Leere list items (kein Text) → alle Defaults (null/false).
 */
function computeDominantStyle(li: ProseMirrorNode): { fontSize: string | null; bold: boolean; italic: boolean } {
    const fontSizes = new Set<string | null>();
    let allBold = true;
    let allItalic = true;
    let textNodeCount = 0;

    li.descendants(child => {
        if (!child.isText) return true;
        textNodeCount++;
        const fontSizeMark = child.marks.find(m => m.type.name === 'textStyle' && m.attrs.fontSize);
        fontSizes.add(fontSizeMark ? fontSizeMark.attrs.fontSize : null);
        if (!child.marks.some(m => m.type.name === 'bold')) allBold = false;
        if (!child.marks.some(m => m.type.name === 'italic')) allItalic = false;
        return false;
    });

    if (textNodeCount === 0) {
        return { fontSize: null, bold: false, italic: false };
    }

    return {
        fontSize: fontSizes.size === 1 ? Array.from(fontSizes)[0] : null,
        bold: allBold,
        italic: allItalic,
    };
}

/** style-Strings vorsichtig zusammenkleben — bestehender Wert bleibt. */
function appendStyle(existing: string | undefined, addition: string): string {
    if (!existing) return addition;
    return `${existing.replace(/;\s*$/, '')}; ${addition}`;
}
